import * as _ from 'lodash';
import { useSelector } from 'react-redux';

import Locator from '../../../../../shared/Locator';
import { useAddToast } from '../../../../contexts/ToastContext';
import { put } from '../../../../redux/api';
import { getProjectType, getReportContext } from '../../../../redux/reportGeneration/selectors';
import { nuc } from '../../../../utils';
import type { SetState } from '../../../../types';
import type { ProjectContext, ReportCategory, ReportExclusionOptions } from '../shared/types';

import useReportContainer from './context/useReportContainer';
import LicensingFilters from './LicensingFilters';
import SBOMFilters from './SBOMFilters';
import FixPlanFilters from './shared/components/FixPlanFilter';
import { ProjectType, ReportCategories } from './utils';
import type {
  DependencyMetadataFilterOption,
  FormatName,
  IncludeFilterOption,
  IncludeFixPlanFilterOption,
} from './shared/types';

type ReportFiltersProps = {
  isApplyButtonDisabled: boolean;
  reportType: ReportCategory;
  setIsDirty: SetState<boolean>;
  isDirty: boolean;
};

const ReportFilters = ({
  isApplyButtonDisabled,
  reportType,
  setIsDirty,
  isDirty,
}: ReportFiltersProps) => {
  const {
    format,
    setFormat,
    includeOptions: appliedIncludeOptions,
    setIncludeOptions,
    excludeOptions: appliedExcludeOptions,
    setExcludeOptions,
    dependencyMetadataOptions: appliedDependencyMetadataOptions,
    setDependencyMetadataOptions,
    fixPlanOptions: appliedFixPlanOptions,
    setFixPlanOptions,
    authorName,
    authorEmail,
    supplier,
    setAuthorName,
    setAuthorEmail,
    setSupplier,
    setDependencyInfoOptions,
  } = useReportContainer();
  const projectType = useSelector(getProjectType);
  const currentProjectContext = useSelector(getReportContext) as ProjectContext;
  const isSBOM = reportType === ReportCategories.SBOM;
  const isLicensing = reportType === ReportCategories.Licensing;
  const isFixPlan = reportType === ReportCategories.RemediationGuidance;
  const addToast = useAddToast();

  const onApply = async ({
    format: newFormat,
    includeOptions,
    excludeOptions,
    dependencyMetadataOptions,
    authorName: newAuthorName,
    authorEmail: newAuthorEmail,
    supplier: newSupplier,
  }: {
    format: FormatName;
    includeOptions: IncludeFilterOption[];
    excludeOptions?: ReportExclusionOptions;
    dependencyMetadataOptions: DependencyMetadataFilterOption[];
    authorName?: string;
    authorEmail?: string;
    supplier?: string;
  }) => {
    setFormat((previousFormat) => (previousFormat === newFormat ? previousFormat : newFormat));
    setIncludeOptions((previousOptions) =>
      _.isEqual(previousOptions, includeOptions) ? previousOptions : includeOptions,
    );
    setDependencyMetadataOptions((previousOptions) =>
      _.isEqual(previousOptions, dependencyMetadataOptions) ? previousOptions : dependencyMetadataOptions,
    );

    // Update SBOM fields if they're provided
    if (newAuthorName !== undefined) {
      setAuthorName(newAuthorName);
    }
    if (newAuthorEmail !== undefined) {
      setAuthorEmail(newAuthorEmail);
    }
    if (newSupplier !== undefined) {
      setSupplier(newSupplier);
    }

    if (excludeOptions) {
      setExcludeOptions((previousOptions) =>
        _.isEqual(previousOptions, excludeOptions) ? previousOptions : excludeOptions,
      );
    }
    if (projectType === ProjectType.Project) {
      try {
        const locator = Locator.parsePackageString(currentProjectContext.revision);
        await put(`/api/projects/${nuc(locator)}`, { bom_column_settings: dependencyMetadataOptions });
        setDependencyInfoOptions(dependencyMetadataOptions);
      } catch (error) {
        addToast({
          content: 'Failed to save dependency metadata',
          type: 'error',
        });
      }
    }
  };

  const onApplyFixPlan = async ({
    format: newFormat,
    fixPlanOptions,
  }: {
    format: FormatName;
    fixPlanOptions: IncludeFixPlanFilterOption[];
  }) => {
    setFormat((previousFormat) => (previousFormat === newFormat ? previousFormat : newFormat));
    setFixPlanOptions((previousOptions) =>
      _.isEqual(previousOptions, fixPlanOptions) ? previousOptions : fixPlanOptions,
    );
  };

  if (isSBOM) {
    return (
      <SBOMFilters
        onApply={onApply}
        appliedFormat={format}
        includeOptions={appliedIncludeOptions}
        dependencyMetadataOptions={appliedDependencyMetadataOptions}
        setIsDirty={setIsDirty}
        isApplyButtonDisabled={isApplyButtonDisabled}
        authorName={authorName}
        authorEmail={authorEmail}
        supplier={supplier}
        projectType={projectType}
        isDirty={isDirty}
      />
    );
  }
  if (isLicensing) {
    return (
      <LicensingFilters
        onApply={onApply}
        appliedFormat={format}
        includeOptions={appliedIncludeOptions}
        dependencyMetadataOptions={appliedDependencyMetadataOptions}
        excludeOptions={appliedExcludeOptions}
        setIsDirty={setIsDirty}
        isDirty={isDirty}
        isApplyButtonDisabled={isApplyButtonDisabled}
      />
    );
  }
  if (isFixPlan) {
    return (
      <FixPlanFilters
        onApply={onApplyFixPlan}
        appliedFormat={format}
        fixPlanOptions={appliedFixPlanOptions}
        setIsDirty={setIsDirty}
        isApplyButtonDisabled={isApplyButtonDisabled}
      />
    );
  }

  return null;
};

export default ReportFilters;
